// Premium Adult Content Creator Landing Page - Dark Luxury Noir Design
// Gio Pilos Official Site - Athens-based Creator
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./PublicLanding.css";
import { api } from "../lib/api";
import AdAffiliatePlacements from "../components/AdAffiliatePlacements";
import AdCarousel from "../components/AdCarousel";
import AdSlot from "../components/AdSlot";
import AdPlaceholder from "../components/AdPlaceholder";
import { toast } from "sonner";
import {
  Lock, ExternalLink, Send, Calendar, Camera, MessageSquare, Bell,
  Trash2, ImagePlus, Mail, Phone, AtSign, MapPin, Clock, Heart,
  X as CloseIcon, ShoppingBag, Crown, Image as ImageIcon, ShoppingCart,
  Flame, Film, Gem, Twitter, Instagram, Music, Bird, Sparkles, UserPlus, LogIn, ArrowRight,
  ChevronLeft, ChevronRight, Check, Star, Zap, Eye, Play, Globe,
} from "lucide-react";
import GrainOverlay from "../components/GrainOverlay";
import Footer from "../components/Footer";
import GlobalFooter from "../components/GlobalFooter";
import MembershipModal from "../components/MembershipModal";
import { PubInput, PubTextarea } from "../components/BookMeExperience";
import { themeVars, DEFAULT_THEME } from "../lib/landingThemes";

// Icon mapper
const getIconComponent = (iconName) => {
  const iconMap = {
    crown: Crown, imageicon: ImageIcon, shoppingcart: ShoppingCart, flame: Flame,
    film: Film, gem: Gem, twitter: Twitter, x: Twitter, instagram: Instagram,
    music: Music, tiktok: Music, bird: Bird, bluesky: Bird,
    onlyfansvip: Crown, onlyfansfree: ImageIcon, justforfans: Film,
    rawfuckclub: Flame, faphouse: Film, top4fans: Gem,
  };
  return iconMap[iconName.toLowerCase().replace(/\s/g, "")] || ExternalLink;
};

export default function PublicLanding() {
  const [config, setConfig] = useState(null);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageForm, setMessageForm] = useState({ name: "", email: "", message: "", photo: null });
  const [sending, setSending] = useState(false);
  const [toyPlacements, setToyPlacements] = useState([]);

  useEffect(() => {
    loadConfig();
    loadToyPlacements();
  }, []);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/api/public/landing");
      setConfig(data);
    } catch (error) {
      console.error("Failed to load landing config:", error);
    }
  };

  const loadToyPlacements = async () => {
    try {
      const { data } = await api.get("/api/ads-affiliate/public?placement=landing");
      // Filter for "my_toy" type placements
      const toys = data.filter(item => item.type === "my_toy" && item.active);
      setToyPlacements(toys);
    } catch (error) {
      console.error("Failed to load toy placements:", error);
    }
  };

  const trackClick = async (section, label) => {
    try {
      await api.post("/api/public/landing/track", { section, label });
    } catch (err) {
      console.error("Track click failed:", err);
    }
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("name", messageForm.name);
      formData.append("email", messageForm.email);
      formData.append("message", messageForm.message);
      if (messageForm.photo) formData.append("photo", messageForm.photo);

      await api.post("/api/public/messages", formData);
      toast.success("Message sent successfully!");
      setMessageForm({ name: "", email: "", message: "", photo: null });
      setShowMessageModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen bg-[#08090D] flex items-center justify-center">
        <div className="text-zinc-500 text-sm">Loading...</div>
      </div>
    );
  }

  const theme = config.theme || DEFAULT_THEME;

  return (
    <div className="min-h-screen bg-[#08090D] text-white relative overflow-hidden">
      {/* Noise Overlay */}
      <GrainOverlay />

      {/* 18+ Age Verification Banner */}
      <div className="fixed top-0 left-0 right-0 bg-[#08090D]/90 backdrop-blur-2xl border-b border-white/10 z-50 py-2">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#E11D48] animate-pulse" />
            <span className="text-zinc-400 font-mono uppercase tracking-[0.25em]">18+ Adult Content</span>
          </div>
          <Link to="/members/login" className="text-[#E11D48] hover:text-[#F43F5E] font-semibold uppercase tracking-wider flex items-center gap-1 transition-colors">
            <Lock size={12} /> Members
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section with Large Image - 90vh */}
        <section 
          className="relative overflow-hidden min-h-[650px] max-h-[900px]" 
          style={{ height: config.hero_size === "full" ? "100vh" : config.hero_size === "medium" ? "95vh" : "90vh" }}
          data-testid="public-landing-hero"
        >
          {/* Large Hero Image */}
          <div className="absolute inset-0">
            <img
              src={config.creator_avatar || "https://ugc.production.linktr.ee/fa9e46b7-5c80-45ed-ba63-d2f8b54d3f08_IMG-0442.jpeg?io=true&size=avatar-hero-v1_0"}
              alt="Gio Pilos"
              className="w-full h-full object-cover object-top"
            />
            
            {/* Top Vignette */}
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#08090D]/80 via-transparent to-transparent z-10" />
            
            {/* Bottom Dark Gradient Overlay - Deep and Strong */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#08090D] via-[#08090D]/65 to-transparent" />
            
            {/* Configurable Color Accent Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-current via-transparent to-transparent opacity-20" 
                 style={{ color: config.hero_gradient_color || "#FF10F0" }} />
          </div>

          {/* Content Overlay - Anchored at Very Bottom */}
          <div className="absolute bottom-0 left-0 right-0 z-20 pb-8 px-6 sm:px-12 lg:px-20">
            <div className="max-w-7xl mx-auto w-full">
              {/* Social Icons - Minimal spacing above */}
              <div className="flex items-center gap-3 mb-4" data-testid="social-icons-bar">
                {config.socials?.filter(s => s.enabled && s.url).map((social, idx) => {
                  const getSocialIcon = (key) => {
                    switch(key?.toLowerCase()) {
                      case 'twitter': case 'x': return <Twitter size={18} className="text-white" />;
                      case 'instagram': return <Instagram size={18} className="text-white" />;
                      case 'email': return <Mail size={18} className="text-white" />;
                      case 'telegram': return <MessageSquare size={18} className="text-white" />;
                      case 'tiktok': return <Film size={18} className="text-white" />;
                      case 'youtube': return <Film size={18} className="text-white" />;
                      default: return <Globe size={18} className="text-white" />;
                    }
                  };

                  // Ensure URL is valid
                  let finalUrl = social.url;
                  if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
                    // If no protocol, assume https
                    finalUrl = `https://${finalUrl}`;
                  }

                  return (
                    <a
                      key={idx}
                      href={finalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/10 hover:bg-[#E11D48]/20 backdrop-blur-md border border-white/20 hover:border-[#E11D48]/50 rounded-full flex items-center justify-center transition-all duration-200"
                      data-testid={`social-link-${social.key}`}
                      onClick={() => trackClick('social', social.key)}
                    >
                      {getSocialIcon(social.key)}
                    </a>
                  );
                })}
              </div>

              {/* Name - Compact spacing */}
              <h1 
                className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-none text-white mb-2"
                style={{ fontFamily: "'Syne', 'Cabinet Grotesk', 'Outfit', sans-serif" }}
                data-testid="hero-creator-name"
              >
                Gio Pilos
              </h1>
              
              {/* Location & Verified Badges */}
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-[0.25em] bg-rose-500/10 text-rose-400 border border-rose-500/30 font-semibold">
                  Athens 🇬🇷
                </span>
                <span className="px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-[0.25em] bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 font-semibold flex items-center gap-1">
                  <Check size={10} /> Verified
                </span>
              </div>

              {/* Bio - More compact */}
              <p 
                className="text-sm sm:text-base text-slate-300 font-light leading-relaxed max-w-2xl mb-4"
                style={{ fontFamily: "'DM Sans', 'Inter', sans-serif" }}
                data-testid="hero-creator-bio"
              >
                Bold adult content creator and actor delivering authentic energy in high-quality productions across various platforms.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setShowMembershipModal(true)}
                  data-testid="hero-become-member-free-button"
                  className="bg-gradient-to-r from-rose-600 via-pink-600 to-rose-500 hover:from-rose-500 hover:to-pink-500 text-white font-bold tracking-wider py-3 px-6 rounded-full shadow-lg shadow-rose-900/40 hover:shadow-rose-600/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 uppercase text-xs flex items-center justify-center gap-2"
                >
                  <Crown size={16} />
                  Become A Member For Free
                  <ArrowRight size={16} />
                </button>
                
                <Link
                  to="/members/login"
                  data-testid="hero-member-login-button"
                  className="bg-purple-600 hover:bg-purple-500 text-white font-bold tracking-wider py-3 px-6 rounded-full shadow-lg shadow-purple-900/40 hover:shadow-purple-600/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 uppercase text-xs flex items-center justify-center gap-2"
                >
                  <LogIn size={16} />
                  Member Login
                </Link>
                
                <button
                  onClick={() => setShowMessageModal(true)}
                  data-testid="hero-dm-me-button"
                  className="bg-white/10 hover:bg-white/20 text-white font-semibold backdrop-blur-md border border-white/20 hover:border-white/40 py-3 px-6 rounded-full transition-all duration-200 text-xs flex items-center justify-center gap-2"
                >
                  <MessageSquare size={16} />
                  DM Me
                </button>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce z-20">
            <ChevronRight size={24} className="text-white/40 rotate-90" />
          </div>
        </section>

        {/* Platform Links Hub - Bento Grid */}
        {config?.links && config.links.length > 0 && (
          <section className="py-16 sm:py-24 px-4 sm:px-8 bg-[#0E1017]">
            <div className="max-w-7xl mx-auto">
              {/* Section Header */}
              <div className="text-center mb-12">
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-rose-500 font-semibold mb-3">
                  Platforms
                </div>
                <h2 
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50 mb-3"
                  style={{ fontFamily: "'Syne', 'Cabinet Grotesk', sans-serif" }}
                >
                  Find Me On
                </h2>
                <p className="text-sm text-slate-400">Exclusive content across multiple platforms</p>
              </div>

              {/* Platform Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {config.links.map((link, idx) => {
                const Icon = getIconComponent(link.icon_name || link.key || link.label);
                const configuredIcon = link.icon;
                const isIconGlyph = configuredIcon && !/^[a-z][a-z0-9_-]*$/i.test(configuredIcon);
                const description = link.description || link.sub;
                const isPremium = link.label.toLowerCase().includes("vip") || link.label.toLowerCase().includes("premium");
                
                return (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick("platform_links", link.label)}
                    data-testid={`platform-card-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={`group relative bg-[#0E1017]/80 backdrop-blur-xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] rounded-2xl hover:border-rose-500/40 transition-all duration-300 p-6 ${isPremium ? "ring-1 ring-rose-500/20" : ""}`}
                  >
                    {isPremium && (
                      <div className="absolute -top-2 -right-2 px-3 py-1 bg-gradient-to-r from-[#E11D48] to-[#F43F5E] rounded-full text-[10px] font-bold uppercase text-white shadow-lg">
                        Premium
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#E11D48]/20 to-[#F43F5E]/10 rounded-2xl flex items-center justify-center border border-rose-500/20">
                        {link.thumb_url ? (
                          <img src={link.thumb_url} alt="" className="w-full h-full rounded-2xl object-cover" />
                        ) : isIconGlyph ? (
                          <span className="text-2xl" aria-hidden="true">{configuredIcon}</span>
                        ) : (
                          <Icon size={28} className="text-[#E11D48]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xl font-semibold text-slate-100 group-hover:text-[#E11D48] transition-colors mb-1 truncate">
                          {link.label}
                        </div>
                        {description && (
                          <div className="text-xs text-slate-400 truncate">{description}</div>
                        )}
                      </div>
                      <ExternalLink size={20} className="text-slate-600 group-hover:text-[#E11D48] transition-colors shrink-0" />
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
        )}

        {/* Ad Carousel - Landing Page */}
        <section className="py-16 px-4 sm:px-8 bg-[#08090D]">
          <div className="max-w-6xl mx-auto">
            <AdSlot placement="landing" />
          </div>
        </section>

        {/* Work With Me Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-8 bg-[#08090D]">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-rose-500 font-semibold mb-3">
                Collaborate
              </div>
              <h2 
                className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50 mb-4"
                style={{ fontFamily: "'Syne', 'Cabinet Grotesk', sans-serif" }}
              >
                Work With Me
              </h2>
              <p className="text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
                Interested in collaborating on content, booking shoots, or exploring partnership opportunities? Apply to work with me and let's create something amazing together.
              </p>
            </div>

            <a
              href="https://giopilos.com/collabapply"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="work-with-me-apply-button"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-rose-600 via-pink-600 to-rose-500 hover:from-rose-500 hover:to-pink-500 text-white font-bold tracking-wider rounded-full shadow-lg shadow-rose-900/40 hover:shadow-rose-600/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 uppercase text-sm"
            >
              <UserPlus size={18} />
              Apply to Work With Me
              <ExternalLink size={16} />
            </a>
          </div>
        </section>

        {/* My Toy Section - Dynamic from Ads Manager */}
        {toyPlacements.length > 0 && (
          <section className="py-16 sm:py-24 px-4 sm:px-8 bg-[#0E1017]">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-rose-500 font-semibold mb-3">
                  Featured Product
                </div>
                <h2 
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50 mb-3"
                  style={{ fontFamily: "'Syne', 'Cabinet Grotesk', sans-serif" }}
                >
                  My Toy
                </h2>
                <p className="text-sm text-slate-400">Premium pleasure products I personally recommend</p>
              </div>

              {toyPlacements.map((toy) => (
                <div key={toy.placement_id} className="bg-[#131622] border border-white/10 rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.5)] mb-6">
                  <div className="grid md:grid-cols-2 gap-8 p-8 sm:p-12">
                    {/* Product Image */}
                    <div className="relative aspect-square bg-gradient-to-br from-rose-500/10 to-pink-500/5 rounded-2xl flex items-center justify-center border border-white/5 overflow-hidden">
                      {toy.media_url ? (
                        <img 
                          src={toy.media_url} 
                          alt={toy.title}
                          className="w-full h-full object-cover rounded-2xl"
                        />
                      ) : (
                        <ShoppingBag size={80} className="text-rose-500/30" />
                      )}
                      <div className="absolute top-4 right-4 px-3 py-1 bg-[#D4AF37]/20 border border-[#D4AF37]/40 rounded-full text-xs font-bold text-[#D4AF37]">
                        Premium
                      </div>
                    </div>

                    {/* Product Details */}
                    <div className="flex flex-col justify-center">
                      <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>
                        {toy.title}
                      </h3>
                      <p className="text-slate-400 mb-6 leading-relaxed whitespace-pre-wrap">
                        {toy.description || "Discover the products I trust and use. Premium quality, body-safe materials, and designed for maximum satisfaction. Perfect for solo play or partnered fun."}
                      </p>

                      {/* Features - If styling_notes contains JSON array */}
                      {toy.styling_notes && (() => {
                        try {
                          const features = JSON.parse(toy.styling_notes);
                          if (Array.isArray(features) && features.length > 0) {
                            return (
                              <ul className="space-y-3 mb-8">
                                {features.map((feature, i) => (
                                  <li key={i} className="flex items-center gap-3 text-sm text-slate-300">
                                    <Check size={16} className="text-rose-500 shrink-0" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            );
                          }
                        } catch (e) {
                          return null;
                        }
                        return null;
                      })()}

                      {toy.destination_url && (
                        <a
                          href={toy.destination_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackClick("my_toy", toy.title)}
                          data-testid="my-toy-shop-button"
                          className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-rose-600 via-pink-600 to-rose-500 hover:from-rose-500 hover:to-pink-500 text-white font-bold tracking-wider rounded-full shadow-lg shadow-rose-900/40 hover:shadow-rose-600/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 uppercase text-sm"
                        >
                          <ShoppingCart size={18} />
                          {toy.button_label || "Shop Now"}
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* About Section */}
        {config.about_text && (
          <section className="py-16 sm:py-24 px-4 sm:px-8 bg-[#08090D]">
            <div className="max-w-4xl mx-auto">
              <div className="bg-[#131622] border border-white/10 rounded-3xl p-8 sm:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
                <h2 
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50 mb-6"
                  style={{ fontFamily: "'Syne', 'Cabinet Grotesk', sans-serif" }}
                >
                  About
                </h2>
                <div className="text-sm sm:text-base text-slate-400 font-normal leading-relaxed space-y-4">
                  {config.about_text.split("\n").map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
                
                {/* Studio Credits */}
                <div className="mt-8 pt-8 border-t border-white/10">
                  <div className="text-xs font-mono uppercase tracking-[0.25em] text-rose-500 font-semibold mb-4">
                    Featured In
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {["LucasEntertainment", "TimTales", "RawAlphaMales", "EricVideos"].map((studio, i) => (
                      <span key={i} className="px-4 py-2 bg-[#0E1017] border border-white/10 rounded-full text-xs font-bold text-slate-400 hover:border-rose-500/30 hover:text-rose-400 transition-all">
                        {studio}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Ad Placements */}
        {config.show_ads && (
          <section className="py-16 sm:py-24 px-4 bg-[#0E1017]">
            <div className="max-w-6xl mx-auto">
              <AdAffiliatePlacements position="landing_main" />
            </div>
          </section>
        )}

        {/* Footer */}
        <section className="py-12 px-4 border-t border-white/10 bg-[#08090D]">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8">
              <div className="flex items-center gap-4">
                {config.social_links?.twitter && (
                  <a href={config.social_links.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#131622] border border-white/10 rounded-full flex items-center justify-center hover:border-[#E11D48] hover:bg-[#E11D48]/10 transition-all">
                    <Twitter size={18} className="text-slate-400 hover:text-[#E11D48] transition-colors" />
                  </a>
                )}
                {config.social_links?.instagram && (
                  <a href={config.social_links.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#131622] border border-white/10 rounded-full flex items-center justify-center hover:border-[#E11D48] hover:bg-[#E11D48]/10 transition-all">
                    <Instagram size={18} className="text-slate-400 hover:text-[#E11D48] transition-colors" />
                  </a>
                )}
              </div>
              
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <Link to="/privacy" className="hover:text-[#E11D48] transition-colors">Privacy</Link>
                <span>·</span>
                <Link to="/terms" className="hover:text-[#E11D48] transition-colors">Terms</Link>
                <span>·</span>
              </div>
            </div>
            
            <div className="text-center text-xs text-slate-600 font-mono">
              © {new Date().getFullYear()} Gio Pilos. All rights reserved. 18+ only.
            </div>
          </div>
        </section>
      </div>

      {/* Membership Modal */}
      {showMembershipModal && (
        <MembershipModal isOpen={showMembershipModal} onClose={() => setShowMembershipModal(false)} />
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setShowMessageModal(false)}>
          <div className="w-full max-w-lg bg-[#131622] border border-white/10 rounded-3xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.8)]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>Send Message</h3>
              <button onClick={() => setShowMessageModal(false)} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors">
                <CloseIcon size={18} className="text-slate-400" />
              </button>
            </div>
            
            <form onSubmit={handleMessageSubmit} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Your Name</label>
                <input
                  type="text"
                  value={messageForm.name}
                  onChange={(e) => setMessageForm({ ...messageForm, name: e.target.value })}
                  required
                  data-testid="message-name"
                  className="w-full px-4 py-3 bg-[#0E1017] border border-white/10 rounded-xl text-white placeholder:text-slate-600 focus:border-rose-500/50 focus:outline-none text-sm"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Your Email</label>
                <input
                  type="email"
                  value={messageForm.email}
                  onChange={(e) => setMessageForm({ ...messageForm, email: e.target.value })}
                  required
                  data-testid="message-email"
                  className="w-full px-4 py-3 bg-[#0E1017] border border-white/10 rounded-xl text-white placeholder:text-slate-600 focus:border-rose-500/50 focus:outline-none text-sm"
                  placeholder="john@example.com"
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Message</label>
                <textarea
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  rows={4}
                  data-testid="message-text"
                  className="w-full px-4 py-3 bg-[#0E1017] border border-white/10 rounded-xl text-white placeholder:text-slate-600 focus:border-rose-500/50 focus:outline-none resize-none text-sm"
                  placeholder="Your message..."
                />
              </div>
              
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Attach Photo (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setMessageForm({ ...messageForm, photo: e.target.files[0] })}
                  data-testid="dm-modal-file-input"
                  className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-rose-500/10 file:text-rose-400 hover:file:bg-rose-500/20 file:cursor-pointer"
                />
              </div>
              
              <button
                type="submit"
                disabled={sending}
                data-testid="dm-modal-submit-button"
                className="w-full py-4 bg-gradient-to-r from-rose-600 via-pink-600 to-rose-500 hover:from-rose-500 hover:to-pink-500 rounded-full font-bold uppercase tracking-wider disabled:opacity-50 hover:shadow-lg hover:shadow-rose-900/40 transition-all duration-200 flex items-center justify-center gap-2 text-white"
              >
                <Send size={18} />
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Global Footer */}
      <GlobalFooter />
    </div>
  );
}

// PubInput, PubTextarea are imported from BookMeExperience.jsx (line 23)

function PubSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider mb-1 text-slate-500">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2.5 rounded-xl bg-[#0E1017] border border-white/10 text-white focus:border-rose-500/50 focus:outline-none text-sm">
        {options.map(o => <option key={o.value} value={o.value} style={{ background: "#0E1017" }}>{o.label}</option>)}
      </select>
    </label>
  );
}
